import { useState } from 'react'
import { Form, Select, DatePicker, Input, Button, Row, Col, Space } from 'antd'
import { ClearOutlined } from '@ant-design/icons'
import type { FeedbackType, FeedbackStatus, FeedbackPriority, FeedbackCategory } from '@/types/feedback.types'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

/**
 * Filter state interface
 */
export interface FeedbackFilterState {
  types?: FeedbackType[]
  statuses?: FeedbackStatus[]
  priorities?: FeedbackPriority[]
  categories?: FeedbackCategory[]
  dateRange?: [string, string] | null
  orgId?: string
  search?: string
  feedbackId?: string  // FB-12345 or just 12345
}

/**
 * Props for FeedbackFilters component
 */
export interface FeedbackFiltersProps {
  isAdminView: boolean
  filters: FeedbackFilterState
  onFilterChange: (filters: FeedbackFilterState) => void
}

/**
 * FeedbackFilters Component
 *
 * Filter bar with two variants:
 * - Customer view: Type, Status, Date Range, Search (4 filters)
 * - Admin view: Adds Priority, Category, Organization (7 filters total)
 *
 * Default status filter: Hides Resolved and Won't Fix for cleaner active view
 */
export const FeedbackFilters: React.FC<FeedbackFiltersProps> = ({
  isAdminView,
  filters,
  onFilterChange,
}) => {
  const [form] = Form.useForm()

  // Controlled open state for dropdowns - close after selection
  const [typesOpen, setTypesOpen] = useState(false)
  const [statusesOpen, setStatusesOpen] = useState(false)
  const [prioritiesOpen, setPrioritiesOpen] = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)

  const typeOptions: FeedbackType[] = ['Enhancement', 'Bug', 'Error']
  const statusOptions: FeedbackStatus[] = ['New', 'In Progress', 'Duplicate', 'Resolved']
  // Priority options include "Unset" for filtering items without priority
  const priorityOptions: (FeedbackPriority | 'Unset')[] = ['Unset', 'Low', 'Medium', 'High', 'Critical']
  const categoryOptions: FeedbackCategory[] = [
    'Pricing',
    'Reports',
    'Data Import',
    'Connections',
    'User Interface',
    'Performance',
    'Other',
  ]

  const handleValuesChange = (_changedValues: any, allValues: any) => {
    // Safely extract date range (both dates must be present)
    let dateRange: [string, string] | null = null
    if (allValues.dateRange && allValues.dateRange[0] && allValues.dateRange[1]) {
      dateRange = [
        allValues.dateRange[0].startOf('day').toISOString(),  // Start of first day
        allValues.dateRange[1].endOf('day').toISOString()     // End of last day (23:59:59.999)
      ]
    }

    const newFilters: FeedbackFilterState = {
      types: allValues.types,
      statuses: allValues.statuses,
      priorities: allValues.priorities,
      categories: allValues.categories,
      dateRange,
      // Treat empty strings as undefined (no filter)
      orgId: allValues.orgId?.trim() || undefined,
      search: allValues.search?.trim() || undefined,
      feedbackId: allValues.feedbackId?.trim() || undefined,
    }
    onFilterChange(newFilters)
  }

  const handleClear = () => {
    // Explicitly set form values to defaults (resetFields uses initialValues which may be stale)
    form.setFieldsValue({
      types: undefined,
      statuses: ['New', 'In Progress'],
      priorities: undefined,
      categories: undefined,
      dateRange: null,
      orgId: undefined,
      search: undefined,
      feedbackId: undefined,
    })
    onFilterChange({
      statuses: ['New', 'In Progress'],
      dateRange: null,
    })
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
      initialValues={{
        types: filters.types,
        statuses: filters.statuses || ['New', 'In Progress'],
        priorities: filters.priorities,
        categories: filters.categories,
        dateRange: filters.dateRange
          ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]
          : null,
        orgId: filters.orgId,
        search: filters.search,
        feedbackId: filters.feedbackId,
      }}
    >
      <Row gutter={[16, 16]}>
        {/* Row 1: Common filters */}
        <Col xs={24} sm={12} md={4}>
          <Form.Item name="feedbackId" label="ID" style={{ marginBottom: 0 }}>
            <Input placeholder="FB-12345" allowClear style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={5}>
          <Form.Item name="types" label="Type" style={{ marginBottom: 0 }}>
            <Select
              mode="multiple"
              placeholder="All types"
              options={typeOptions.map((t) => ({ label: t, value: t }))}
              allowClear
              open={typesOpen}
              onOpenChange={setTypesOpen}
              onSelect={() => setTypesOpen(false)}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="statuses" label="Status" style={{ marginBottom: 0 }}>
            <Select
              mode="multiple"
              placeholder="All statuses"
              options={statusOptions.map((s) => ({ label: s, value: s }))}
              allowClear
              open={statusesOpen}
              onOpenChange={setStatusesOpen}
              onSelect={() => setStatusesOpen(false)}
            />
          </Form.Item>
        </Col>

        {isAdminView && (
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="priorities" label="Priority" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                placeholder="All priorities"
                options={priorityOptions.map((p) => ({ label: p, value: p }))}
                allowClear
                open={prioritiesOpen}
                onOpenChange={setPrioritiesOpen}
                onSelect={() => setPrioritiesOpen(false)}
              />
            </Form.Item>
          </Col>
        )}

        {isAdminView && (
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="categories" label="Category" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                placeholder="All categories"
                options={categoryOptions.map((c) => ({ label: c, value: c }))}
                allowClear
                open={categoriesOpen}
                onOpenChange={setCategoriesOpen}
                onSelect={() => setCategoriesOpen(false)}
              />
            </Form.Item>
          </Col>
        )}

        <Col xs={24} sm={12} md={6}>
          <Form.Item name="dateRange" label="Date Range" style={{ marginBottom: 0 }}>
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        {/* Row 2: Search and actions */}
        {isAdminView && (
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="orgId" label="Organization" style={{ marginBottom: 0 }}>
              <Input placeholder="Org UID or name" allowClear />
            </Form.Item>
          </Col>
        )}

        <Col xs={24} sm={12} md={isAdminView ? 6 : 12}>
          <Form.Item name="search" label="Description" style={{ marginBottom: 0 }}>
            <Input placeholder="Search description" allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item label=" " style={{ marginBottom: 0 }}>
            <Space>
              <Button icon={<ClearOutlined />} onClick={handleClear}>
                Clear
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default FeedbackFilters
